import * as Sentry from "@sentry/nextjs"
import { Spinner } from "flowbite-react"
import { Formik, FormikHelpers, FormikProps } from "formik"
import { useRouter } from "next/router"
import React, { useMemo } from "react"
import useSWR from "swr"
import * as Yup from "yup"

import useToast from "@hooks/useToast"
import { CartItemDetailsType } from "@modules/tenants/OrdersPage/Components/AddOrderModal"
import * as OrderServices from "@mono/essentials/services/OrderServices"
import { PaymentTypes, ValidateGiftcardCodeResponseType } from "@mono/essentials/types/CartTypes"
import {
  CreateOrderRequestType,
  OrderStatuses,
  OrderStatusTypes,
} from "@mono/essentials/types/OrderTypes"
import { CustomerTypeOrder, PayOrderRequestType } from "@mono/essentials/types/OrderTypes"

import { PaymentAmountDetails } from "../PaymentModal"

import SquareCreditCardFormData from "./SquareCreditCardFormData"

export type SquareCreditCardFormAttributes = {
  name: string
  paymentType: PaymentTypes
  amount: string
  isUsingSavedCard?: boolean
  savedCardId: string
}

interface SquareCreditCardFormProps {
  onSuccess?: (paymentId: string, values: SquareCreditCardFormAttributes) => Promise<void> | void
  handleClose: () => void
  clientId: string
  locationId: string
  hideDefaultContainer: boolean
  cartDetails?: CartItemDetailsType
  cartId?: string
  refetchCartData?: () => void
  paymentAmountDetails?: PaymentAmountDetails
  goToPrevStep?: () => void
  hideBackButton?: boolean
  sqaureUpRef?: any
  payOrderTipDetails?: PayOrderRequestType["tip"]
  onlyTipPayment?: boolean
  isIntegratedPage?: boolean
  customer?: CustomerTypeOrder
  squarePaymentId?: string
  squareUpFormRef?: React.RefObject<FormikProps<SquareCreditCardFormAttributes>>
  hideSavedCards?: boolean
  giftcardDetails?: ValidateGiftcardCodeResponseType["giftcard"]
  isAuthUser?: boolean
  showPendingBookingPaymentConfirmation?: (arg0: boolean) => void
  handlePaymentConfirmation?: boolean
  isWidgetsCard?: boolean
}

const SquareCreditCardForm = ({
  onSuccess,
  handleClose,
  clientId,
  locationId,
  hideDefaultContainer = false,
  cartDetails,
  cartId,
  refetchCartData,
  paymentAmountDetails,
  goToPrevStep,
  sqaureUpRef,
  hideBackButton = false,
  payOrderTipDetails,
  onlyTipPayment = false,
  isIntegratedPage,
  customer,
  squarePaymentId,
  squareUpFormRef,
  hideSavedCards = false,
  giftcardDetails,
  isAuthUser,
  showPendingBookingPaymentConfirmation,
  handlePaymentConfirmation = false,
  isWidgetsCard = false,
}: SquareCreditCardFormProps) => {
  const { showToast } = useToast()
  const router = useRouter()

  const {
    data: savedCardsInfo,
    isLoading: isSavedCardsInfoLoading,
    mutate: refetchSavedCardsInfo,
  } = useSWR(
    Boolean(customer?.id) && squarePaymentId && !hideSavedCards && isAuthUser
      ? `savedCardsInfo-${squarePaymentId}`
      : null,
    async () => {
      const { data: getSavedcardsResponse } = await OrderServices.getSavedCardsOfCustomer(
        customer.id,
        squarePaymentId
      )
      if (getSavedcardsResponse) {
        return getSavedcardsResponse
      }
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshWhenOffline: false,
      refreshWhenHidden: false,
      refreshInterval: 0,
      shouldRetryOnError: false,
    }
  )

  const defaultCardId = useMemo(() => {
    if (savedCardsInfo?.length) {
      const id = savedCardsInfo.find((card) => card.is_default)?.id
      return id || savedCardsInfo[0].id
    }
    return ""
  }, [savedCardsInfo])

  const initialValues: SquareCreditCardFormAttributes = useMemo(
    () => ({
      savedCardId: defaultCardId,
      name: "",
      paymentType: paymentAmountDetails?.isDepositEnabledForItems
        ? PaymentTypes.DEPOSIT_AMOUNT
        : PaymentTypes.FULL_AMOUNT,
      amount: giftcardDetails
        ? paymentAmountDetails?.isDepositEnabledForItems
          ? `${giftcardDetails.remaining_deposit_total.amount_in_units}`
          : `${giftcardDetails.remaining_total.amount_in_units}`
        : `${
            paymentAmountDetails?.isDepositEnabledForItems
              ? paymentAmountDetails?.depostAmountWithTip
              : paymentAmountDetails?.totalAmountWithTip
          }`,
      isUsingSavedCard: Boolean(savedCardsInfo?.length),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [giftcardDetails, savedCardsInfo, paymentAmountDetails]
  )

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        name: Yup.string().when("savedCardId", (savedCardId, field) =>
          !savedCardId ? field.required("*Card Name is Required") : field
        ),
        paymentType: Yup.string().required("*Payment Type is Required"),
        amount: Yup.string()
          .matches(/^\d{1,}(\.\d{0,4})?$/, "Amount is not valid")
          .required("*Amount is Required"),
        isUsingSavedCard: Yup.boolean().optional(),
      }),
    []
  )

  const onSubmit = async (
    values: SquareCreditCardFormAttributes,
    { setSubmitting }: FormikHelpers<SquareCreditCardFormAttributes>
  ) => {
    try {
      setSubmitting(true)
      if (
        values.paymentType !== PaymentTypes.DEPOSIT_AMOUNT ||
        (!isNaN(parseFloat(values.amount)) && parseFloat(values.amount) > 0)
      ) {
        if (values.savedCardId) {
          const selectedCard = savedCardsInfo?.find(
            (savedCard) => savedCard.id === values.savedCardId
          )
          if (handlePaymentConfirmation) {
            showPendingBookingPaymentConfirmation?.(true)
            return
          }
          const nameOnCard = values.isUsingSavedCard ? selectedCard.name_on_card : values.name
          await onSuccess?.(
            savedCardsInfo?.find((savedCard) => savedCard.id === values.savedCardId)?.payment_token,
            {
              ...values,
              amount: giftcardDetails
                ? `${
                    paymentAmountDetails?.isDepositEnabledForItems
                      ? paymentAmountDetails?.depostAmountWithTip
                      : paymentAmountDetails?.totalAmountWithTip
                  }`
                : values.amount,
              name: nameOnCard,
            }
          )
        } else {
          showToast({
            heading: "Payment failed! try again with different card.",
            type: "danger",
          })
        }
      } else {
        const payload: Partial<CreateOrderRequestType> = {
          cart_id: cartId,
          payment_type: values.paymentType,
        }

        const createOrderResponse = await OrderServices.createOrder(
          payload as CreateOrderRequestType
        )
        if (OrderStatuses.includes(createOrderResponse?.data?.order?.status as OrderStatusTypes)) {
          showToast({
            heading: (
              <div className={"flex flex-row items-center justify-between"}>
                Order Created successfully!
              </div>
            ),
          })
          router.push(`/orders/view/${createOrderResponse.data.order.id}`)
        }
      }
    } catch (e: any) {
      Sentry.captureException(e)
      console.error(
        e.message ?? e.response.data.message ?? "Something went wrong! please try again"
      )
      showToast({
        heading: e.message ?? e.response.data.message ?? "Something went wrong! please try again",
        type: "danger",
      })
    } finally {
      setSubmitting(false)
    }
  }

  return isSavedCardsInfoLoading ? (
    <div className={"w-full flex items-center justify-center"}>
      <Spinner aria-label={"Center-aligned spinner example"} className={"text-center my-6"} />
    </div>
  ) : (
    <div className={"relative"}>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        innerRef={squareUpFormRef}
        validateOnChange
        enableReinitialize
      >
        {() => (
          <form
            autoComplete={"off"}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.preventDefault()
            }}
          >
            <SquareCreditCardFormData
              handleClose={handleClose}
              clientId={clientId}
              locationId={locationId}
              onSuccess={onSuccess}
              hideDefaultContainer={hideDefaultContainer}
              cartDetails={cartDetails}
              cartId={cartId}
              refetchCartData={refetchCartData}
              paymentAmountDetails={paymentAmountDetails}
              goToPrevStep={goToPrevStep}
              hideBackButton={hideBackButton}
              sqaureUpRef={sqaureUpRef}
              payOrderTipDetails={payOrderTipDetails}
              onlyTipPayment={onlyTipPayment}
              isIntegratedPage={isIntegratedPage}
              customer={customer}
              squarePaymentId={squarePaymentId}
              savedCardsInfo={savedCardsInfo}
              onSavedCardDelete={() => refetchSavedCardsInfo()}
              showPendingBookingPaymentConfirmation={showPendingBookingPaymentConfirmation}
              handlePaymentConfirmation={handlePaymentConfirmation}
              isWidgetsCard={isWidgetsCard}
            />
          </form>
        )}
      </Formik>
    </div>
  )
}

export default SquareCreditCardForm
